import { useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { client } from '../../api/client';
import { Button } from '../ui/Button';
import { ArrowLeft, Search, Save } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrchestraGroup {
  id: string;
  name: string;
}

interface MemberRow {
  id: string;
  firstName: string;
  lastName: string;
  instrument: string | null;
  groups: OrchestraGroup[];
  status: string | null;
  lockedByAdmin: boolean;
}

interface EventInfo {
  id: string;
  title: string;
  type: string;
  date: string;
  startTime: string;
  location: string | null;
  status: string;
  groups: OrchestraGroup[];
}

interface Summary {
  event: EventInfo;
  members: MemberRow[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'aanwezig', label: 'Aanwezig', short: 'Ja' },
  { value: 'afwezig', label: 'Afwezig', short: 'Nee' },
  { value: 'onder_voorbehoud', label: 'Voorbehoud', short: '?' },
  { value: 'niet_spelend', label: 'NS', short: 'NS' },
];

const STATUS_COLORS: Record<string, { bg: string; border: string; color: string }> = {
  aanwezig:         { bg: '#dcfce7', border: '#16a34a', color: '#15803d' },
  afwezig:          { bg: '#fee2e2', border: '#dc2626', color: '#b91c1c' },
  onder_voorbehoud: { bg: '#fef3c7', border: '#d97706', color: '#92400e' },
  niet_spelend:     { bg: '#dbeafe', border: '#3b82f6', color: '#1e40af' },
};

const TYPE_LABELS: Record<string, string> = {
  rehearsal: 'Repetitie',
  concert: 'Concert',
  uitstap: 'Uitstap',
};

const FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'aanwezig', label: 'Aanwezig' },
  { value: 'afwezig', label: 'Afwezig' },
  { value: 'onder_voorbehoud', label: 'Voorbehoud' },
  { value: 'niet_spelend', label: 'NS' },
  { value: 'onbekend', label: 'Onbekend' },
];

// ── Styled components ─────────────────────────────────────────────────────────

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: #666;
  padding: 0;
  margin-bottom: 1.25rem;
  &:hover { color: ${({ theme }) => theme.colors.primary.royalBlue}; }
`;

const EventMeta = styled.div`
  margin-bottom: 1.5rem;
`;

const EventTitle = styled.h2`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.primary.navy};
  margin: 0 0 0.25rem;
`;

const EventSub = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 0.15rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ $bg?: string }>`
  background: ${({ $bg }) => $bg ?? '#f8f9fa'};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  text-align: center;
`;

const SummaryCount = styled.div<{ $color?: string }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ $color }) => $color ?? '#374151'};
  line-height: 1;
`;

const SummaryLabel = styled.div`
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 0.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

const SearchLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.38rem 0.6rem;
  background: white;
  color: #6b7280;
  flex: 1;
  min-width: 180px;

  input {
    border: 0;
    outline: 0;
    font-size: 0.85rem;
    color: #111827;
    background: transparent;
    width: 100%;
  }
`;

const FilterPill = styled.button<{ $active: boolean }>`
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: 1.5px solid ${({ $active }) => ($active ? '#0f3460' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#0f3460' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
`;

const UnsavedBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fefce8;
  border: 1.5px solid #fbbf24;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  color: #78350f;
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.6rem 0.75rem;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.8rem;
`;

const Td = styled.td`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.875rem;
  vertical-align: middle;
`;

const SectionHeader = styled.tr`
  background: #f0f4ff;
  td {
    padding: 0.4rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const StatusBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
`;

const StatusBtn = styled.button<{ $active: boolean; $status: string; $changed: boolean }>`
  padding: 0.22rem 0.5rem;
  border-radius: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid ${({ $active, $status }) =>
    $active ? (STATUS_COLORS[$status]?.border ?? '#d1d5db') : '#d1d5db'};
  background: ${({ $active, $status }) =>
    $active ? (STATUS_COLORS[$status]?.bg ?? '#f3f4f6') : 'white'};
  color: ${({ $active, $status }) =>
    $active ? (STATUS_COLORS[$status]?.color ?? '#374151') : '#6b7280'};
  outline: ${({ $changed }) => ($changed ? '2px solid #f59e0b' : 'none')};
  outline-offset: 1px;
`;

const ClearBtn = styled.button`
  padding: 0.22rem 0.5rem;
  border-radius: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid #d1d5db;
  background: white;
  color: #9ca3af;
  &:hover { color: #374151; border-color: #9ca3af; }
`;

const SaveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  align-items: center;
  margin-top: 1rem;
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function groupMembers(members: MemberRow[]): Map<string, MemberRow[]> {
  const map = new Map<string, MemberRow[]>();
  for (const m of members) {
    const key = m.instrument || m.groups.map((g) => g.name).join(', ') || 'Overig';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  }
  return map;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const AttendanceDetail = ({
  eventId,
  onBack,
}: {
  eventId: string;
  onBack: () => void;
}) => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [instrumentFilter, setInstrumentFilter] = useState('');
  // draft: memberId -> pending status (null = clear)
  const [draft, setDraft] = useState<Map<string, string | null>>(new Map());

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/attendance/events/${eventId}/summary`);
      setSummary(res.data);
      setDraft(new Map());
      setInstrumentFilter('');
    } catch {
      // empty state
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const setDraftStatus = (memberId: string, status: string | null, currentStatus: string | null) => {
    setDraft((prev) => {
      const next = new Map(prev);
      // If the new status matches the original, remove from draft (no change)
      if (status === currentStatus) {
        next.delete(memberId);
      } else {
        next.set(memberId, status);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!summary || draft.size === 0) return;
    setSaving(true);
    try {
      const overrides = [...draft.entries()].map(([userId, status]) => ({ userId, status }));
      await client.post('/attendance/admin/bulk-override', { eventId, overrides });
      setSummary((prev) =>
        prev
          ? {
              ...prev,
              members: prev.members.map((m) => {
                if (!draft.has(m.id)) return m;
                const newStatus = draft.get(m.id) ?? null;
                return { ...m, status: newStatus, lockedByAdmin: newStatus !== null };
              }),
            }
          : prev
      );
      setDraft(new Map());
    } catch {
      // silent — keep draft so user can retry
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(new Map());
  };

  const members = useMemo(() => summary?.members ?? [], [summary?.members]);
  const effectiveStatus = useCallback((m: MemberRow): string | null =>
    draft.has(m.id) ? draft.get(m.id)! : m.status, [draft]);

  const instrumentKey = useCallback((m: MemberRow) =>
    m.instrument || m.groups.map((g) => g.name).join(', ') || 'Overig', []);

  const instruments = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const m of members) {
      const key = instrumentKey(m);
      if (!seen.has(key)) { seen.add(key); result.push(key); }
    }
    return result.sort();
  }, [instrumentKey, members]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (q && !`${m.firstName} ${m.lastName}`.toLowerCase().includes(q)) return false;
      const eff = effectiveStatus(m);
      if (filter === 'onbekend') return !eff;
      if (filter) return eff === filter;
      if (instrumentFilter && instrumentKey(m) !== instrumentFilter) return false;
      return true;
    });
  }, [members, search, filter, instrumentFilter, effectiveStatus, instrumentKey]);

  if (loading) return <p style={{ color: '#888' }}>Laden…</p>;
  if (!summary) return <p style={{ color: '#888' }}>Kon aanwezigheid niet laden.</p>;

  const { event } = summary;
  const flat = event.groups.some((g) => g.name === 'Trommelkorps');
  const isHarmonie = event.groups.some((g) => g.name === 'Harmonie');
  const hasChanges = draft.size > 0;

  const counts = {
    aanwezig:         members.filter((m) => effectiveStatus(m) === 'aanwezig').length,
    afwezig:          members.filter((m) => effectiveStatus(m) === 'afwezig').length,
    onder_voorbehoud: members.filter((m) => effectiveStatus(m) === 'onder_voorbehoud').length,
    niet_spelend:     members.filter((m) => effectiveStatus(m) === 'niet_spelend').length,
    onbekend:         members.filter((m) => !effectiveStatus(m)).length,
  };

  const dateStr = new Date(event.date).toLocaleDateString('nl-BE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const grouped = flat ? null : groupMembers(filtered);

  const renderRow = (m: MemberRow) => {
    const eff = effectiveStatus(m);
    const changed = draft.has(m.id);
    return (
      <tr key={m.id}>
        <Td>
          {m.firstName} {m.lastName}
          {m.lockedByAdmin && !changed && (
            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#9ca3af' }} title="Ingevuld door bestuur">🔒</span>
          )}
          {changed && (
            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#d97706', fontWeight: 700 }}>●</span>
          )}
        </Td>
        <Td>
          <StatusBtnRow>
            {STATUS_OPTIONS.map((opt) => (
              <StatusBtn
                key={opt.value}
                $active={eff === opt.value}
                $status={opt.value}
                $changed={changed && eff === opt.value}
                onClick={() => setDraftStatus(m.id, eff === opt.value ? null : opt.value, m.status)}
                title={opt.label}
              >
                {opt.short}
              </StatusBtn>
            ))}
            {eff && (
              <ClearBtn onClick={() => setDraftStatus(m.id, null, m.status)} title="Wis status">×</ClearBtn>
            )}
          </StatusBtnRow>
        </Td>
      </tr>
    );
  };

  return (
    <div>
      <BackBtn onClick={onBack}>
        <ArrowLeft size={14} /> Terug naar kalender
      </BackBtn>

      <EventMeta>
        <EventTitle>{event.title}</EventTitle>
        <EventSub>{dateStr} om {event.startTime}</EventSub>
        {event.location && <EventSub>{event.location}</EventSub>}
        <EventSub style={{ marginTop: '0.25rem' }}>
          {TYPE_LABELS[event.type] ?? event.type}
          {event.status === 'cancelled' && (
            <span style={{ marginLeft: '0.5rem', color: '#dc2626', fontWeight: 700 }}>— GEANNULEERD</span>
          )}
        </EventSub>
      </EventMeta>

      <SummaryGrid>
        <SummaryCard $bg="#dcfce7">
          <SummaryCount $color="#15803d">{counts.aanwezig}</SummaryCount>
          <SummaryLabel>Aanwezig</SummaryLabel>
        </SummaryCard>
        <SummaryCard $bg="#fee2e2">
          <SummaryCount $color="#b91c1c">{counts.afwezig}</SummaryCount>
          <SummaryLabel>Afwezig</SummaryLabel>
        </SummaryCard>
        <SummaryCard $bg="#fef3c7">
          <SummaryCount $color="#92400e">{counts.onder_voorbehoud}</SummaryCount>
          <SummaryLabel>Voorbehoud</SummaryLabel>
        </SummaryCard>
        <SummaryCard $bg="#dbeafe">
          <SummaryCount $color="#1e40af">{counts.niet_spelend}</SummaryCount>
          <SummaryLabel>NS</SummaryLabel>
        </SummaryCard>
        <SummaryCard>
          <SummaryCount>{counts.onbekend}</SummaryCount>
          <SummaryLabel>Onbekend</SummaryLabel>
        </SummaryCard>
      </SummaryGrid>

      {hasChanges && (
        <UnsavedBanner>
          <span>● {draft.size} onopgeslagen {draft.size === 1 ? 'wijziging' : 'wijzigingen'}</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button size="sm" variant="ghost" onClick={handleDiscard} disabled={saving}>Ongedaan maken</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              <Save size={13} style={{ marginRight: '0.3rem' }} />
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </div>
        </UnsavedBanner>
      )}

      <Toolbar style={{ marginTop: hasChanges ? '0.75rem' : '0' }}>
        <SearchLabel>
          <Search size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op naam…"
          />
        </SearchLabel>
        {FILTER_OPTIONS.map((opt) => (
          <FilterPill
            key={opt.value}
            $active={filter === opt.value}
            onClick={() => setFilter(opt.value)}
          >
            {opt.label}
          </FilterPill>
        ))}
      </Toolbar>
      {isHarmonie && (
        <Toolbar style={{ marginTop: '0.4rem' }}>
          <FilterPill $active={instrumentFilter === ''} onClick={() => setInstrumentFilter('')}>
            Alle secties
          </FilterPill>
          {instruments.map((instr) => (
            <FilterPill
              key={instr}
              $active={instrumentFilter === instr}
              onClick={() => setInstrumentFilter(instrumentFilter === instr ? '' : instr)}
            >
              {instr}
            </FilterPill>
          ))}
        </Toolbar>
      )}

      <Table>
        <thead>
          <tr>
            <Th>Naam</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {flat
            ? filtered.map(renderRow)
            : [...grouped!.entries()].map(([section, list]) => (
                list.length === 0 ? null : (
                  <>
                    <SectionHeader key={`header-${section}`}>
                      <td colSpan={2}>{section}</td>
                    </SectionHeader>
                    {list.map(renderRow)}
                  </>
                )
              ))
          }
          {filtered.length === 0 && (
            <tr>
              <Td colSpan={2} style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center' }}>
                Geen leden gevonden.
              </Td>
            </tr>
          )}
        </tbody>
      </Table>

      <SaveBar>
        <Button variant="ghost" onClick={fetchSummary} disabled={saving}>Vernieuwen</Button>
        {hasChanges && (
          <>
            <Button variant="ghost" onClick={handleDiscard} disabled={saving}>Ongedaan maken</Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save size={13} style={{ marginRight: '0.3rem' }} />
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </>
        )}
      </SaveBar>
    </div>
  );
};
